import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { cn } from '@/shared/lib/cn'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'
import { useThemeStore } from '@/shared/lib/themeStore'
import type { ThemeMode } from '@/shared/lib/themeUtils'
import { useGuardedNavigate } from '@/shared/lib/useGuardedNavigate'
import { useWhatsNewUnseen } from '@/shared/lib/whatsNewSeen'
import { UserAvatar } from '@/shared/ui/UserAvatar'
import { ChevronsUpDownIcon, MonitorIcon, MoonIcon, SunIcon } from '@/shared/ui/icons'
import { useConfirmDialog } from '@/shared/ui/useConfirmDialog'
import { useSessionStore } from '@/features/session/model/sessionStore'
import type { UserRole } from '@/entities/session/types'
import {
  getAccountMenuItems,
  type AccountMenuItem,
} from '@/widgets/navigation/accountMenuItems'

export type AccountMenuVariant = 'sidebar' | 'header'

const THEME_OPTIONS: { mode: ThemeMode; label: string; icon: ComponentType<{ size?: number }> }[] = [
  { mode: 'light', label: 'Claro', icon: SunIcon },
  { mode: 'dark', label: 'Escuro', icon: MoonIcon },
  { mode: 'system', label: 'Sistema', icon: MonitorIcon },
]

const MENU_ITEM_SELECTOR = '[role="menuitem"], [role="menuitemradio"]'

interface AccountMenuProps {
  variant: AccountMenuVariant
  name: string
  /** E-mail no topo do menu, para saber em que conta se está. */
  email?: string | null
  avatarUrl?: string | null
  crm?: string | null
  role: UserRole
  isSystemAdmin: boolean
  collapsed?: boolean
}

export const AccountMenu = ({
  variant,
  name,
  email,
  avatarUrl,
  crm,
  role,
  isSystemAdmin,
  collapsed = false,
}: AccountMenuProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const themeMode = useThemeStore((s) => s.mode)
  const setThemeMode = useThemeStore((s) => s.setMode)
  const version = __APP_VERSION__
  const whatsNewUnseen = useWhatsNewUnseen(version)
  const logout = useSessionStore((s) => s.logout)
  const { confirm } = useConfirmDialog()
  const guardedNavigate = useGuardedNavigate()

  const items = useMemo(
    () => getAccountMenuItems({ role, isSystemAdmin }),
    [role, isSystemAdmin],
  )

  const menuOpen = open

  useEffect(() => {
    if (!menuOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current?.contains(event.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen])

  // Ao abrir, o foco vai ao primeiro item (teclado: setas, Home e End percorrem o menu).
  useEffect(() => {
    if (!menuOpen) return
    const raf = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector<HTMLElement>(MENU_ITEM_SELECTOR)?.focus()
    })
    return () => window.cancelAnimationFrame(raf)
  }, [menuOpen])

  const onMenuKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(MENU_ITEM_SELECTOR) ?? [])
    if (items.length === 0) return
    const current = items.indexOf(document.activeElement as HTMLElement)
    let next: number | null = null
    if (event.key === 'ArrowDown') next = current < items.length - 1 ? current + 1 : 0
    else if (event.key === 'ArrowUp') next = current <= 0 ? items.length - 1 : current - 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = items.length - 1
    else if (event.key === 'Tab') {
      setOpen(false)
      return
    }
    if (next === null) return
    event.preventDefault()
    items[next]?.focus()
  }, [])

  const { closeThen } = useOverlay(menuOpen, {
    kind: 'menu',
    label: `Menu da conta de ${name}`,
    onRequestClose: () => setOpen(false),
  })

  const handleLogout = useCallback(async () => {
    const ok = await confirm({
      title: 'Confirmar logout',
      description: 'Deseja realmente sair da sua conta?',
      confirmLabel: 'Sair',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    })
    if (ok) await logout()
  }, [confirm, logout])

  const onItemClick = useCallback(
    (item: AccountMenuItem) => {
      if (item.kind === 'logout') {
        closeThen(() => {
          void handleLogout()
        })
        return
      }
      closeThen(() => {
        void guardedNavigate(item.to)
      })
    },
    [closeThen, guardedNavigate, handleLogout],
  )

  const triggerLabel = `Menu da conta de ${name}${whatsNewUnseen ? ' (novidades não vistas)' : ''}`

  const unseenDot = whatsNewUnseen ? (
    <span
      aria-hidden
      data-testid="account-whats-new-dot"
      className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-[var(--color-bg-surface)] bg-[var(--color-mint-strong)]"
    />
  ) : null

  const trigger: ReactNode =
    variant === 'header' ? (
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={triggerLabel}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80"
      >
        <span className="relative">
          <UserAvatar name={name} avatarUrl={avatarUrl} className="h-[27px] w-[27px] rounded-full" />
          {unseenDot}
        </span>
      </button>
    ) : (
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={triggerLabel}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        className={cn(
          'flex w-full min-h-11 items-center gap-3 rounded-2xl px-3 py-2 text-left text-sm font-semibold text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-page)]',
          collapsed && 'justify-center px-2',
        )}
      >
        <span className={cn('relative shrink-0', collapsed && 'mx-auto')}>
          <UserAvatar name={name} avatarUrl={avatarUrl} size="sidebar" />
          {unseenDot}
        </span>
        {!collapsed ? (
          <>
            <span className="min-w-0 flex-1 leading-snug">
              <span className="line-clamp-2 whitespace-normal text-[var(--color-text-primary)]">
                {name}
              </span>
              {crm ? (
                <span className="block truncate text-xs font-medium text-[var(--color-text-muted)]">
                  CRM {crm}
                </span>
              ) : null}
            </span>
            <ChevronsUpDownIcon size={16} className="shrink-0 opacity-70" aria-hidden />
          </>
        ) : null}
      </button>
    )

  return (
    <div ref={ref} className={cn('relative', variant === 'sidebar' && 'w-full')}>
      {trigger}
      {menuOpen ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label={`Menu da conta de ${name}`}
          onKeyDown={onMenuKeyDown}
          className={cn(
            'absolute z-30 w-max min-w-[15rem] max-w-[min(20rem,calc(100vw-1.5rem))] rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-1 shadow-lg',
            variant === 'sidebar'
              ? cn('bottom-full mb-1', collapsed ? 'left-0' : 'left-0 right-0 w-auto')
              : 'left-0 top-full mt-1',
          )}
        >
          {email ? (
            <p
              className="truncate px-3 pb-1.5 pt-2 text-xs font-medium text-[var(--color-text-muted)]"
              title={email}
            >
              {email}
            </p>
          ) : null}
          {items.map((item, index) => {
            const isLogout = item.kind === 'logout'
            const prev = index > 0 ? items[index - 1] : null
            const startsGroup =
              item.kind === 'link' && prev?.kind === 'link' && prev.group !== item.group
            const isWhatsNew = item.key === 'whats-new'
            return (
              <div key={item.key}>
                {startsGroup || (email && index === 0) ? <MenuSeparator /> : null}
                {isLogout ? (
                  <>
                    <MenuSeparator />
                    <div role="group" aria-label="Tema" className="px-3 pb-1 pt-1.5">
                      <p className="pb-1.5 text-xs font-semibold text-[var(--color-text-muted)]">Tema</p>
                      <div className="grid grid-cols-3 gap-1 rounded-lg bg-[var(--color-page)] p-0.5">
                        {THEME_OPTIONS.map((opt) => {
                          const active = themeMode === opt.mode
                          return (
                            <button
                              key={opt.mode}
                              type="button"
                              role="menuitemradio"
                              aria-checked={active}
                              onClick={() => void setThemeMode(opt.mode)}
                              className={cn(
                                'flex h-8 items-center justify-center gap-1 rounded-md text-xs font-semibold transition-colors',
                                active
                                  ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
                                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
                              )}
                            >
                              <opt.icon size={14} aria-hidden />
                              {opt.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                    <MenuSeparator />
                  </>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  aria-label={
                    isWhatsNew
                      ? `Novidades, versão ${version}${whatsNewUnseen ? ', não vistas' : ''}`
                      : undefined
                  }
                  onClick={() => onItemClick(item)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold outline-none transition-colors focus-visible:bg-[var(--color-mint-soft)]',
                    isLogout
                      ? 'text-[var(--color-destructive)] hover:bg-[var(--color-mint-soft)]'
                      : 'text-[var(--color-text-primary)] hover:bg-[var(--color-mint-soft)]',
                  )}
                >
                  <span className="flex shrink-0 opacity-80 [&>svg]:size-[18px]" aria-hidden>
                    <item.icon size={18} />
                  </span>
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {isWhatsNew ? (
                    <span
                      className="ml-auto flex shrink-0 items-center gap-1.5 pl-3 text-xs font-medium tabular-nums text-[var(--color-text-muted)]"
                      data-testid="account-menu-version"
                    >
                      v{version}
                      {whatsNewUnseen ? (
                        <span
                          aria-hidden
                          data-testid="account-menu-whats-new-dot"
                          className="size-2 rounded-full bg-[var(--color-mint-strong)]"
                        />
                      ) : null}
                    </span>
                  ) : null}
                </button>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

const MenuSeparator = () => <div className="mx-1 my-1 h-px bg-[var(--color-border)]" aria-hidden />
